/**
 * useGlobalTracker - Auto-track user interactions globally
 * Tracks button clicks, link clicks, page navigation, and errors
 * Respects analytics consent and follows castrol-flow naming
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from './gaTagManager';
import {
  TRACKING_EVENTS,
  isAnalyticsConsented,
  getButtonContext,
  getLinkContext
} from './trackingConfig';

export const useGlobalTracker = () => {
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);
  const trackedClicksRef = useRef(new Set());

  // Track page navigation
  useEffect(() => {
    if (location.pathname !== prevLocationRef.current) {
      if (isAnalyticsConsented()) {
        trackEvent(TRACKING_EVENTS.PAGE_NAVIGATION, {
          from_page: prevLocationRef.current,
          to_page: location.pathname,
          source: 'route_change'
        });
      }
      prevLocationRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Auto-track button clicks
  const handleButtonClick = useCallback((e) => {
    const button = e.target.closest('button');
    if (!button || !isAnalyticsConsented()) return;

    // Avoid duplicate tracking
    const clickId = `${button.id || button.textContent}:${Date.now()}`;
    if (trackedClicksRef.current.has(clickId)) return;
    trackedClicksRef.current.add(clickId);
    setTimeout(() => trackedClicksRef.current.delete(clickId), 100);

    const buttonName = button.getAttribute('data-tracking-name') 
      || button.id 
      || button.getAttribute('aria-label')
      || button.textContent?.trim() 
      || 'unnamed';

    trackEvent(TRACKING_EVENTS.BUTTON_CLICK, getButtonContext(button, buttonName));
  }, []);

  // Auto-track link clicks (excluding same-page hash links)
  const handleLinkClick = useCallback((e) => {
    const link = e.target.closest('a');
    if (!link || !isAnalyticsConsented()) return;

    const href = link.getAttribute('href');
    const normalizedHref = href?.trim() || '';
    let isScriptProtocol = false;
    if (normalizedHref && typeof window !== 'undefined') {
      try {
        const scriptProtocol = ['java', 'script:'].join('');
        isScriptProtocol = new URL(normalizedHref, window.location.origin).protocol === scriptProtocol;
      } catch (error) {
        isScriptProtocol = false;
      }
    }
    if (!normalizedHref || normalizedHref.startsWith('#') || isScriptProtocol) return;

    const linkName = link.getAttribute('data-tracking-name')
      || link.id
      || link.getAttribute('aria-label')
      || link.textContent?.trim()
      || normalizedHref;

    // Track external vs internal navigation
    const isExternal = normalizedHref.startsWith('http');
    trackEvent(TRACKING_EVENTS.LINK_CLICK, {
      link_name: linkName,
      link_href: normalizedHref,
      is_external: isExternal,
      ...getLinkContext(link, linkName)
    });
  }, []);

  // Auto-track errors globally
  useEffect(() => {
    const handleError = (event) => {
      if (!isAnalyticsConsented()) return;

      trackEvent(TRACKING_EVENTS.PAGE_NAVIGATION, {
        error_type: 'uncaught_error',
        error_message: event.message?.substring(0, 200) || 'Unknown error',
        error_filename: event.filename || 'unknown',
        error_lineno: event.lineno || 0,
        source: 'global_error_handler'
      });
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Attach event listeners
  useEffect(() => {
    document.addEventListener('click', handleButtonClick);
    document.addEventListener('click', handleLinkClick);

    return () => {
      document.removeEventListener('click', handleButtonClick);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [handleButtonClick, handleLinkClick]);

  // Manual track function for custom events
  const trackCustomEvent = useCallback((eventName, eventData = {}) => {
    if (isAnalyticsConsented()) {
      trackEvent(eventName, eventData);
    }
  }, []);

  return { trackCustomEvent };
};

export default useGlobalTracker;
